import React, { memo, useCallback, useState, useEffect, useRef } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Select, Button, message, Spin, Tooltip } from 'antd';
import { SoundOutlined, PlayCircleFilled, LoadingOutlined, AudioOutlined, FileTextOutlined } from '@ant-design/icons';
import NodeLabel from './NodeLabel';
import api from '../../../api/client';
import { useParams } from 'react-router-dom';
import { useNodeTitle } from './useNodeTitle';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';

const { Option } = Select;

const TextToSpeechNode = ({ id, data, selected }) => {
    const { setNodes } = useReactFlow();
    const allEdges = useEdges();
    const allNodes = useNodes();
    const { onSave } = data;
    const { projectId: canvasId } = useParams(); // Get canvas ID from URL
    const onTitleChange = useNodeTitle(id, data);

    const [audioUrl, setAudioUrl] = useState(data.audioUrl || null);
    const [voiceId, setVoiceId] = useState(data.voiceId || 'JBFqnCBsd6RMkjVDRZzb');
    const [generating, setGenerating] = useState(false);
    const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
    const [voices, setVoices] = useState([]);
    const [voicesLoading, setVoicesLoading] = useState(false);
    const [hovered, setHovered] = useState(false);

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    // Initial voices (fallback/starters)
    const defaultVoices = [
        { voice_id: 'JBFqnCBsd6RMkjVDRZzb', name: 'Adam' },
        { voice_id: 'XrExE9yKIg1WjnnlVkGX', name: 'Matilda' },
        { voice_id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel' },
        { voice_id: 'AZnzlk1XvdvUeBnXmlld', name: 'Domi' }
    ];

    useEffect(() => {
        // Fetch voices (optional, can relay on defaults if offline or error)
        const fetchVoices = async () => {
            setVoicesLoading(true);
            try {
                const res = await api.get('/elevenlabs/voices');
                if (res.data && res.data.voices) {
                    setVoices(res.data.voices);
                } else {
                    setVoices(defaultVoices);
                }
            } catch (err) {
                console.error('Failed to fetch voices', err);
                setVoices(defaultVoices);
            } finally {
                setVoicesLoading(false);
            }
        };
        fetchVoices();
    }, []);

    const updateStoreData = useCallback((key, value) => {
        setNodes((nodes) =>
            nodes.map((node) => {
                if (node.id === id) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            [key]: value,
                        },
                    };
                }
                return node;
            })
        );
    }, [id, setNodes]);

    const persistNodeData = useCallback((key, value) => {
        if (onSave) {
            onSave(id, { [key]: value });
        }
    }, [id, onSave]);

    const handleVoiceChange = (value) => {
        setVoiceId(value);
        updateStoreData('voiceId', value);
        persistNodeData('voiceId', value);
    };

    // --- Audio Playback Control ---
    // --- Audio Playback Control ---
    const audioRef = useRef(null);

    useEffect(() => {
        if (shouldAutoPlay && audioUrl && audioRef.current) {
            audioRef.current.play()
                .then(() => setShouldAutoPlay(false))
                .catch(e => console.error("Auto-play failed:", e));
        }
    }, [audioUrl, shouldAutoPlay]);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            // Find input from left handle
            const inputEdge = allEdges.find(
                (edge) => edge.target === id && (edge.targetHandle === 'left-input' || edge.targetHandle === null)
            );

            let textToSpeak = '';

            if (inputEdge) {
                const sourceNode = allNodes.find((node) => node.id === inputEdge.source);

                if (inputEdge.sourceHandle && inputEdge.sourceHandle.startsWith('slice-')) {
                    // It's a slice!
                    const sliceIndex = parseInt(inputEdge.sourceHandle.split('-')[1], 10);
                    if (sourceNode && sourceNode.data && sourceNode.data.slices && sourceNode.data.slices[sliceIndex]) {
                        textToSpeak = sourceNode.data.slices[sliceIndex];
                    }
                } else if (sourceNode && sourceNode.data) {
                    // Try to get text from various potential source fields
                    textToSpeak = sourceNode.data.text || sourceNode.data.output || sourceNode.data.code || '';
                }
            } else if (data.text) {
                // Fallback to internal text
                textToSpeak = data.text;
            }

            if (!textToSpeak) {
                message.warning('No text input found. Connect a Text Node.');
                setGenerating(false);
                return;
            }

            const response = await api.post('/elevenlabs/generate', {
                text: textToSpeak,
                voiceId: voiceId,
                canvasId: canvasId,
                nodeId: id
            });

            if (response.data && response.data.url) {
                const url = response.data.url;
                setAudioUrl(url);
                setShouldAutoPlay(true);
                updateStoreData('audioUrl', url);
                persistNodeData('audioUrl', url);
                message.success('Audio generated and saved!');
            }


        } catch (error) {
            console.error('Generation failed:', error);
            message.error('Failed to generate audio.');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div
            style={{
                background: 'transparent',
                padding: 20,
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={250}
                minHeight={180}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Text to Speech'}
                iconType={data.iconType || 'sound'}
                color={data.nodeColor || '#eb2f96'}
                onTitleChange={onTitleChange}
            />

            {/* Large Left Input Handle */}
            <Handle
                type="target"
                position={Position.Left}
                id="left-input"
                style={getHandleStyle(TYPE_COLORS.text, 'left', handlesVisible)}
            />

            {/* Large Right Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                id="right-output"
                style={getHandleStyle(TYPE_COLORS.audio, 'right', handlesVisible)}
            />

            {/* Generate Button - Styled as a Handle */}
            <Button
                type="primary"
                shape="circle"
                icon={generating ? <LoadingOutlined style={{ fontSize: 72 }} /> : <PlayCircleFilled style={{ fontSize: 72 }} />}
                onClick={handleGenerate}
                style={{
                    position: 'absolute',
                    right: -180,
                    bottom: 0, // Positioned at the bottom
                    width: 150,
                    height: 150,
                    zIndex: 100,
                    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
                    border: '12px solid #161b22',
                    background: '#eb2f96', // Pink
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            />

            <div style={{
                background: '#161b22',
                borderRadius: 8,
                border: generating ? '1px solid #eb2f96' : '1px solid #30363d',
                boxShadow: generating
                    ? '0 0 15px #eb2f96, 0 0 30px rgba(235, 47, 150, 0.4)'
                    : '0 4px 12px rgba(0,0,0,0.5)',
                display: 'flex',
                flexDirection: 'column',
                padding: 16,
                flex: 1,
                height: '100%',
                gap: 16,
                transition: 'all 0.3s ease',
                animation: generating ? 'pulse-glow 2s infinite' : 'none'
            }}>
                <style>{`
                    @keyframes pulse-glow {
                        0% { box-shadow: 0 0 15px #eb2f96, 0 0 30px rgba(235, 47, 150, 0.4); border-color: #eb2f96; }
                        50% { box-shadow: 0 0 25px #ff85c0, 0 0 50px rgba(235, 47, 150, 0.6); border-color: #ff85c0; }
                        100% { box-shadow: 0 0 15px #eb2f96, 0 0 30px rgba(235, 47, 150, 0.4); border-color: #eb2f96; }
                    }
                `}</style>
                {/* Voice Selector */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} className="nodrag">
                    <span style={{ fontSize: 10, fontWeight: 'bold', color: '#8b949e', textTransform: 'uppercase' }}>Voice</span>
                    <Select
                        value={voiceId}
                        onChange={handleVoiceChange}
                        loading={voicesLoading}
                        style={{ width: '100%' }}
                        className="nodrag"
                        popupClassName="nowheel nodrag"
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        dropdownStyle={{ background: '#1f242e', border: '1px solid #30363d' }}
                    >
                        {(voices.length > 0 ? voices : defaultVoices).map(v => (
                            <Option key={v.voice_id || v.id || v.voiceId} value={v.voice_id || v.id || v.voiceId}>
                                {v.name}
                            </Option>
                        ))}
                    </Select>
                </div>

                {/* Audio Player Area */}
                <div
                    className="nodrag"
                    style={{
                        height: 'auto',
                        minHeight: 54,
                        padding: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#0d1117',
                        borderRadius: 6,
                        border: '1px dashed #30363d',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    {audioUrl ? (
                        <audio
                            ref={audioRef}
                            controls
                            src={audioUrl}
                            style={{ width: '90%', outline: 'none' }}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', color: '#8b949e' }}>
                            <SoundOutlined style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                            <span style={{ fontSize: 12 }}>Generated audio will play here</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default memo(TextToSpeechNode);
